// filepath: apps/web/src/features/admin/recordPermissions/FieldPermissionsDialog.tsx
//
// FR-UI-ADM-2 AC-3 — three-state toggle grid per (role, entity) for
// field-level read/write/hidden permissions.
//
// UI three-state enum maps to wire {can_read, can_write}:
//   - "readable"  → can_read=true,  can_write=false
//   - "writable"  → can_read=true,  can_write=true  (AC-7 invariant)
//   - "hidden"    → row absence on the server (omitted from grants)
//
// AC-5 (writable+hidden defense-in-depth): the save button is
// DISABLED if any field is set to a state that would produce
// can_write=true && can_read=false on the wire (impossible via
// the enum, but defensive). The server still rejects with
// HTTP 400 if a stale client bypasses the SPA gate.
//
// All text renders as JSX text nodes (INV-9).

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { ApiError } from "@/definitions/types";
import {
  useFieldPermissionsMutation,
  useFieldPermissionsQuery,
} from "./queries";
import {
  FIELD_PERMISSION_DATA_STATE,
  type FieldPermissionGrant,
  type FieldPermissionState,
} from "./types";

interface FieldPermissionsDialogProps {
  open: boolean;
  roleName: string;
  entityType: string;
  /** Field list (from entity-type definition; NOT from grants). */
  fields: string[];
  onClose: () => void;
  onSaved: () => void;
  onConflict: (
    serverVersion: number,
    serverGrants: FieldPermissionGrant[],
  ) => void;
}

function grantsToStateMap(
  grants: FieldPermissionGrant[],
): Record<string, FieldPermissionState> {
  const m: Record<string, FieldPermissionState> = {};
  for (const g of grants) {
    if (g.can_write) m[g.field] = "writable";
    else if (g.can_read) m[g.field] = "readable";
    else m[g.field] = "hidden";
  }
  return m;
}

function stateMapToGrants(
  fields: string[],
  states: Record<string, FieldPermissionState>,
): FieldPermissionGrant[] {
  // AC-3 wire shape: hidden = row absence. Only emit grants for
  // readable + writable fields.
  const out: FieldPermissionGrant[] = [];
  for (const f of fields) {
    const s = states[f] ?? "hidden";
    if (s === "readable") {
      out.push({ field: f, can_read: true, can_write: false });
    } else if (s === "writable") {
      out.push({ field: f, can_read: true, can_write: true });
    }
    // s === "hidden" → no row emitted (server DELETEs)
  }
  return out;
}

export function FieldPermissionsDialog({
  open,
  roleName,
  entityType,
  fields,
  onClose,
  onSaved,
  onConflict,
}: FieldPermissionsDialogProps): React.ReactElement | null {
  const { t } = useTranslation();
  const query = useFieldPermissionsQuery(roleName, entityType);
  const mutation = useFieldPermissionsMutation(roleName, entityType);

  const [states, setStates] = useState<Record<string, FieldPermissionState>>(
    {},
  );
  const [version, setVersion] = useState<number>(1);

  useEffect(() => {
    if (open && query.data) {
      setStates(grantsToStateMap(query.data.grants));
      setVersion(query.data.version);
    }
  }, [open, query.data]);

  const dirty = useMemo(() => {
    if (!query.data) return false;
    const current = grantsToStateMap(query.data.grants);
    if (Object.keys(current).length !== Object.keys(states).length) return true;
    for (const f of fields) {
      if ((current[f] ?? "hidden") !== (states[f] ?? "hidden")) return true;
    }
    return false;
  }, [query.data, states, fields]);

  if (!open) return null;

  const setField = (field: string, next: FieldPermissionState) => {
    setStates((prev) => ({ ...prev, [field]: next }));
  };

  const onSave = async () => {
    const grants = stateMapToGrants(fields, states);
    try {
      await mutation.mutateAsync({ grants, version });
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        try {
          const body = JSON.parse(err.message) as {
            current_version?: number;
            server_grants?: FieldPermissionGrant[];
          };
          if (
            body.current_version !== undefined &&
            body.server_grants !== undefined
          ) {
            onConflict(body.current_version, body.server_grants);
          }
        } catch {
          // swallow
        }
      }
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="field-permissions-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="field-permissions-dialog"
    >
      <div className="w-full max-w-2xl rounded-md border border-border bg-background p-4 shadow-lg">
        <h2 id="field-permissions-title" className="text-sm font-semibold">
          {t("admin.field_permissions.title")}
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          {roleName} / {entityType}
        </p>
        <div className="mt-3 max-h-96 overflow-y-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="py-1 pr-2">
                  {t("admin.field_permissions.field")}
                </th>
                <th className="py-1 pr-2">
                  {t("admin.field_permissions.state")}
                </th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field) => {
                const state = states[field] ?? "hidden";
                return (
                  <tr
                    key={field}
                    className="border-b border-border"
                    data-testid={`field-permission-row-${field}`}
                    data-state={FIELD_PERMISSION_DATA_STATE[state]}
                  >
                    <td className="py-1 pr-2 font-mono">{field}</td>
                    <td className="py-1 pr-2">
                      <div
                        className="flex gap-1"
                        role="radiogroup"
                        aria-label={field}
                      >
                        {(["readable", "writable", "hidden"] as const).map(
                          (s) => (
                            <button
                              key={s}
                              type="button"
                              role="radio"
                              aria-checked={state === s}
                              className={`rounded border border-border px-2 py-1 ${
                                state === s
                                  ? "bg-primary text-primary-foreground"
                                  : ""
                              }`}
                              onClick={() => setField(field, s)}
                              data-state={FIELD_PERMISSION_DATA_STATE[s]}
                              data-testid={`field-permission-${field}-${s}`}
                            >
                              {t(`admin.field_permissions.state.${s}`)}
                            </button>
                          ),
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="rounded border border-border px-3 py-1 text-xs"
            onClick={onClose}
            data-testid="field-permissions-close"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="rounded bg-primary px-3 py-1 text-xs text-primary-foreground disabled:opacity-50"
            onClick={onSave}
            disabled={mutation.isPending || !dirty}
            data-testid="field-permissions-save"
          >
            {t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
